import type { DynamicEntityRow } from '../../../../api/strapiDynamicEntity';
import { parseSurveyTeamScheduledSlots } from '../../../../api/strapiCrm';

export type TeamPresenceRowState = {
  isPresence: boolean;
  pictureFile: File | null;
};

export type SurveyTeamDateGroup = {
  /** `YYYY-MM-DD` atau `__none__` jika tanpa tanggal. */
  dateKey: string;
  tabLabel: string;
  teams: DynamicEntityRow[];
};

export function buildInitialTeamPresence(
  teams: DynamicEntityRow[],
  defaultPresentTeamDocumentId: string,
): Record<string, TeamPresenceRowState> {
  const next: Record<string, TeamPresenceRowState> = {};
  for (const team of teams) {
    const id = String(team.documentId ?? '').trim();
    if (!id) continue;
    next[id] = {
      isPresence: id === defaultPresentTeamDocumentId,
      pictureFile: null,
    };
  }
  return next;
}

function formatDateOnlyLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Label tanggal (+ jam) sampling dari `survey_teams.scheduled_date`. */
export function formatSurveyTeamScheduledDateLabel(team: DynamicEntityRow): string {
  const slots = parseSurveyTeamScheduledSlots(team.scheduled_date);
  if (slots.length === 0) return '—';
  return slots
    .map((slot) => {
      const dateLabel = formatDateOnlyLabel(slot.date);
      return slot.timeLabel ? `${dateLabel} · ${slot.timeLabel}` : dateLabel;
    })
    .join('; ');
}

/** Tanggal primer untuk grouping tab (slot pertama). */
export function primarySurveyTeamScheduledDate(team: DynamicEntityRow): string | null {
  const slots = parseSurveyTeamScheduledSlots(team.scheduled_date);
  return slots[0]?.date ?? null;
}

/** Group petugas by tanggal sampling untuk tab di modal mulai sampling. */
export function groupSurveyTeamsByScheduledDate(teams: DynamicEntityRow[]): SurveyTeamDateGroup[] {
  const byDate = new Map<string, DynamicEntityRow[]>();
  for (const team of teams) {
    const date = primarySurveyTeamScheduledDate(team) ?? '__none__';
    const list = byDate.get(date) ?? [];
    list.push(team);
    byDate.set(date, list);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => {
      if (a === '__none__') return 1;
      if (b === '__none__') return -1;
      return a.localeCompare(b);
    })
    .map(([dateKey, groupTeams]) => ({
      dateKey,
      tabLabel: dateKey === '__none__' ? 'Tanpa tanggal' : formatDateOnlyLabel(dateKey),
      teams: groupTeams,
    }));
}
